'use client';

import React from 'react';
import classNames from 'classnames';

export interface RangeIndicatorProps {
  max?: number;
  min?: number;
  value: number;
}

export const RangeIndicator: React.FC<RangeIndicatorProps> = ({ max = 100, min = 0, value }) => {
  const arrayOfTailwindColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-400', 'bg-lime-500', 'bg-green-600'];

  const pickColor: string = React.useMemo(() => {
    const colorsMapHex = {
      0: '#EF4444',
      1: '#F97316',
      2: '#FBBF24',
      3: '#84CC16',
      4: '#059669',
    };
    const percentage = (value - min) / (max - min);
    const index = Math.min(Math.floor(percentage * 5), 4);
    return colorsMapHex[index];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, min, max]);

  return (
    <div className="range-indicator inline-flex flex-col relative">
      <div className="inline-flex justify-between relative z-[1]">
        <TriangleIcon fill={pickColor} value={value} />
        {arrayOfTailwindColors.map((color, index) => (
          <div
            className={classNames(
              `range-indicator-segment py-0.5 flex flex-col items-center text-sm h-4 w-full min-w-[40px] ml-[1px] mr-[1px] ${color}`,
              {
                'rounded-l-full': index === 0,
                'rounded-r-full': arrayOfTailwindColors.length - 1 === index,
              },
            )}
            key={color}
          />
        ))}
      </div>
      <div className="range-indicator-values-container w-full flex justify-between mt-5 text-xs font-semibold text-gray-400">
        <span className="range-indicator-min-value">{min}</span>
        <span className="range-indicator-max-value">{max}</span>
      </div>
    </div>
  );
};

const TriangleIcon: React.FC<{ className?: string; fill: string; value: number }> = ({ className, fill, value }) => (
  <span
    className={classNames('absolute top-2 translate-x-[-50%]', { [`${className}`]: !!className })}
    style={{ left: `${value + 0.5}%` }}
  >
    <svg fill="none" height="14" viewBox="0 0 14 12" width="16" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8.299 1.25a1.5 1.5 0 00-2.598 0l-4.33 7.5A1.5 1.5 0 002.67 11h8.66a1.5 1.5 0 001.3-2.25l-4.331-7.5z"
        fill={fill}
        stroke="#F2F8FF"
      ></path>
    </svg>
    <div
      className={classNames('range-indicator-value text-sm font-semibold text-black mt-[-3px]', {
        'ml-[-2px]': value === 100,
      })}
    >
      {value}
    </div>
  </span>
);
